import { isOk, Ok, Result } from "@pacote/result"
import { useQuery } from "@tanstack/react-query"
import { z } from "zod"

import { ListboxOption } from "@/components/ui/select-field"

import {
  ApiFailure,
  BaseApiErrorResponse,
  baseApiResponseSchema,
  HandledApiError,
  UnhandledApiError,
} from "./api-config"
import { baseClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

const citySchema = z.object({
  id: z.string(),
  description: z.string(),
  fiscalCode: z.string(),
  province: z.string(),
  cap: z.string(),
  actual: z.boolean(),
})

const allCitiesResponseSchema = baseApiResponseSchema.extend({
  cities: z.array(citySchema),
})

type AllCitiesResponse = z.infer<typeof allCitiesResponseSchema>

type City = z.infer<typeof citySchema>

type AllCitiesServiceResponse = Result<
  {
    cities: AllCitiesResponse["cities"]
    normalizedCities: { [key: string]: ListboxOption & City }
    normalizedAllCities: { [key: string]: ListboxOption & City }
  },
  void
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const allCitiesServiceEndpoint = "/residence-core-ms/v1/geo/allCities"

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const allCitiesFetcher = async (): Promise<AllCitiesServiceResponse> => {
  const responseResult = await baseClientFetcher<
    AllCitiesResponse,
    BaseApiErrorResponse
  >({
    url: allCitiesServiceEndpoint,
    successSchemaValidator: allCitiesResponseSchema,
    enrichHeaders: true,
  })

  if (isOk(responseResult)) {
    const { cities } = responseResult.value

    const actualCities = cities.filter((city) => city.actual)

    const normalizedCities: { [key: string]: ListboxOption & City } = {}
    const normalizedAllCities: { [key: string]: ListboxOption & City } = {}

    cities.forEach((city) => {
      const cityLabel = `${city.description} (${city.province})`
      normalizedAllCities[cityLabel] = {
        label: cityLabel,
        optionId: city.fiscalCode,
        ...city,
      }
      if (city.actual) {
        normalizedCities[cityLabel] = {
          label: cityLabel,
          optionId: city.fiscalCode,
          ...city,
        }
      }
    })

    return Ok({ normalizedCities, normalizedAllCities, cities: actualCities })
  }

  const { status } = responseResult.value

  if (status != 422)
    throw new UnhandledApiError({
      originalStatus: status as number,
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 status was returned at ${allCitiesServiceEndpoint}`,
    })

  throw new HandledApiError({ data: responseResult.value, type: "Failure" })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/
const useAllCitiesQuery = (enabled = true) => {
  const { data, isLoading, isFetching, ...rest } = useQuery({
    queryKey: [allCitiesServiceEndpoint],
    queryFn: allCitiesFetcher,
    enabled,
    staleTime: Infinity,
  })

  const normalizedCities = data && isOk(data) ? data.value.normalizedCities : {}
  const normalizedAllCities =
    data && isOk(data) ? data.value.normalizedAllCities : {}

  return {
    cities: data,
    normalizedCities,
    normalizedAllCities,
    isAllCitiesQueryLoading: isLoading || isFetching,
    ...rest,
  }
}

export { allCitiesFetcher, useAllCitiesQuery }
export type { AllCitiesResponse, AllCitiesServiceResponse }
